import { http } from '../client'
import { USE_MOCKS } from '../config'
import { endpoints } from '../endpoints'
import { categories } from '../mock/fixtures'
import { placeholderImage } from '../mock/placeholder'
import { delay, makeId, mockDb } from '../mock/db'
import { ApiError } from '../types'
import type {
  AdminProductListItem,
  AdminProductPayload,
  Product,
  UploadedImage,
} from '../types'

/**
 * Administração de produtos cadastrados à mão.
 *
 * Estes produtos convivem no mesmo catálogo dos que vêm da API externa; o campo
 * `source` distingue os dois.
 */

export function listAdminProducts(search?: string): Promise<AdminProductListItem[]> {
  if (!USE_MOCKS) {
    return http.get<AdminProductListItem[]>(endpoints.admin.products, {
      query: { q: search },
    })
  }

  const term = search?.trim().toLowerCase() ?? ''
  const items = mockDb.state.manualProducts
    .filter((p) => !term || p.title.toLowerCase().includes(term))
    .map<AdminProductListItem>((p) => ({
      id: p.id,
      title: p.title,
      categoryName: p.categoryName,
      quantity: p.availableQuantity ?? 0,
      price: p.price,
      imageUrl: p.images[0] ?? null,
    }))

  return delay(items, 300)
}

export function createAdminProduct(payload: AdminProductPayload): Promise<Product> {
  if (!USE_MOCKS) return http.post<Product>(endpoints.admin.products, payload)

  const category = categories.find((c) => c.id === payload.categoryId) ?? null
  const id = makeId('man')

  const product: Product = {
    id,
    slug: id,
    title: payload.title,
    categoryId: category?.id ?? null,
    categoryName: category?.name ?? null,
    price: payload.price,
    originalPrice: payload.discountPercent
      ? Math.round((payload.price / (1 - payload.discountPercent / 100)) * 100) / 100
      : null,
    discountPercent: payload.discountPercent || null,
    images: payload.images.length ? payload.images : [placeholderImage(id, payload.title)],
    rating: payload.rating || null,
    ratingCount: 0,
    soldCount: 0,
    availableQuantity: payload.quantity,
    badges: [],
    externalUrl: payload.externalUrl,
    deliveryDescription: payload.deliveryDescription ?? null,
    highlights: payload.highlights.filter(Boolean),
    specifications: [],
    description: payload.description ?? null,
    source: 'manual',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }

  mockDb.update({ manualProducts: [product, ...mockDb.state.manualProducts] })
  return delay(product, 600)
}

export function updateAdminProduct(
  id: string,
  payload: Partial<AdminProductPayload>,
): Promise<Product> {
  if (!USE_MOCKS) return http.patch<Product>(endpoints.admin.product(id), payload)

  const list = mockDb.state.manualProducts
  const index = list.findIndex((p) => p.id === id)
  if (index === -1) {
    return Promise.reject(new ApiError('Produto não encontrado.', 404, 'product_not_found'))
  }

  const updated: Product = {
    ...list[index],
    ...payload,
    availableQuantity: payload.quantity ?? list[index].availableQuantity,
    updatedAt: new Date().toISOString(),
  }
  const next = [...list]
  next[index] = updated
  mockDb.update({ manualProducts: next })
  return delay(updated, 400)
}

export function deleteAdminProduct(id: string): Promise<void> {
  if (!USE_MOCKS) return http.delete<void>(endpoints.admin.product(id))

  mockDb.update({
    manualProducts: mockDb.state.manualProducts.filter((p) => p.id !== id),
  })
  return delay(undefined, 300)
}

/**
 * Envia a imagem para o bucket R2.
 *
 * O backend deve receber `multipart/form-data` com o campo `file`, gravar no R2
 * e devolver `{ key, url }`. O frontend guarda apenas a `url`.
 */
export function uploadProductImage(file: File): Promise<UploadedImage> {
  if (!USE_MOCKS) {
    const form = new FormData()
    form.append('file', file)
    return http.post<UploadedImage>(endpoints.admin.uploadImage, form)
  }

  // No mock, transforma o arquivo local em data URL para pré-visualizar.
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () =>
      resolve({ key: `mock/${file.name}`, url: String(reader.result) })
    reader.onerror = () =>
      reject(new ApiError('Falha ao ler o arquivo.', 0, 'file_read_error'))
    reader.readAsDataURL(file)
  })
}
